import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { 
  UserOutlined, 
  MailOutlined, 
  FileTextOutlined, 
  MessageOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import './ContactUsPage.css';

const getEmbedUrl = (mapUrl, address) => {
  if (!mapUrl && !address) return '';
  const url = mapUrl || '';

  if (url.includes('/maps/embed')) return url;

  const coordsMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (coordsMatch) {
    const q = `${coordsMatch[1]},${coordsMatch[2]}`;
    return `https://www.google.com/maps?q=${q}&output=embed`;
  }

  const qMatch = url.match(/[?&]q=([^&]+)/);
  if (qMatch) {
    return `https://www.google.com/maps?q=${qMatch[1]}&output=embed`;
  }

  if (address) {
    return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
  }

  return '';
};

const ContactUsPage = () => {
  const { site_name } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [contactInfo, setContactInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/contact/info`);
        if (response.ok) {
          const data = await response.json();
          setContactInfo(data);
        } else {
          console.error('فشل في جلب معلومات الاتصال:', response.status);
        }
      } catch (error) {
        console.error('خطأ في جلب معلومات الاتصال:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContactInfo();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // مسح رسالة الخطأ عند الكتابة
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // إرسال الرسالة إلى الخادم
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        if (data.errors && data.errors.length > 0) {
          setError(data.errors[0]);
        } else {
          setError(data.message || 'حدث خطأ أثناء إرسال الرسالة');
        }
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم، يرجى المحاولة مرة أخرى');
      console.error('خطأ في إرسال الرسالة:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="contact-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>جاري تحميل معلومات الاتصال...</p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <>
        <Helmet>
          <title>اتصل بنا | {site_name || ''}</title>
        </Helmet>
        <div className="contact-page">
        <div className="contact-hero">
          <div className="container">
            <div className="hero-content">
              <h1 className="hero-title">تم إرسال رسالتك بنجاح</h1>
              <p className="hero-subtitle">شكراً لك على التواصل معنا</p>
            </div>
          </div>
        </div>
        
        <div className="contact-content">
          <div className="container">
            <div className="success-message">
              <CheckCircleOutlined className="success-icon" />
              <h3>تم إرسال رسالتك بنجاح!</h3>
              <p>سنقوم بالرد عليك في أقرب وقت ممكن</p>
              <p className="confirmation-note">
                تم إرسال رسالة تأكيد إلى بريدك الإلكتروني
              </p>
              <button 
                onClick={() => setIsSuccess(false)}
                className="submit-btn"
                style={{ marginTop: '20px' }}
              >
                إرسال رسالة أخرى
              </button>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  const mapEnabled = contactInfo?.map_enabled === true || contactInfo?.map_enabled === '1';
  const embedUrl = mapEnabled ? getEmbedUrl(contactInfo?.mapUrl || '', contactInfo?.address || '') : '';
  const openMapUrl = contactInfo?.mapUrl || (contactInfo?.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.address)}` : '');

  return (
    <>
      <Helmet>
        <title>اتصل بنا | {site_name || ''}</title>
      </Helmet>
      <div className="contact-page">
      {/* Hero Section */}
      <div className="contact-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">اتصل بنا</h1>
            <p className="hero-subtitle">
              نحن هنا للإجابة على استفساراتك والاستماع إلى آرائك
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">24/7</span>
                <span className="stat-label">دعم متواصل</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">100%</span>
                <span className="stat-label">استجابة سريعة</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">1000+</span>
                <span className="stat-label">عميل راضٍ</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Content */}
      <div className="contact-content">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info Section */}
            <div className="contact-info-section">
              <h2 className="section-title">معلومات الاتصال</h2>
              <p className="section-description">
                يمكنك التواصل معنا من خلال أي من الطرق التالية
              </p>
              
              <div className="contact-info-list">
                <div className="contact-info-item">
                  <PhoneOutlined className="info-icon" />
                  <div className="info-content">
                    <h3 className="info-title">الهاتف</h3>
                    <a href={`tel:${contactInfo?.phone || '04262918-771678010'}`} className="info-value">
                      {contactInfo?.phone || '04262918-771678010'}
                    </a>
                  </div>
                </div>
                
                <div className="contact-info-item">
                  <MailOutlined className="info-icon" />
                  <div className="info-content">
                    <h3 className="info-title">البريد الإلكتروني</h3>
                    <a href={`mailto:${contactInfo?.email ||'nab771@gmail.com'}`} className="info-value">
                      {contactInfo?.email ||'nab771@gmail.com'}
                    </a>
                  </div>
                </div>
                
                <div className="contact-info-item">
                  <EnvironmentOutlined className="info-icon" />
                  <div className="info-content">
                    <h3 className="info-title">العنوان</h3>
                    <span className="info-value">
                      {contactInfo?.address || 'اليمن-تعز-شارع جمال-خلف الكريمي-وسوق ديلوكس'}
                    </span>
                  </div>
                </div>
              </div>
              
             
              
            
            </div>

            {/* Contact Form Section */}
            <div className="contact-form-section">
              <h2 className="section-title">أرسل رسالة</h2>
              <p className="section-description">
                املأ النموذج أدناه وسنقوم بالرد عليك في أقرب وقت ممكن
              </p>
              
              {error && (
                <div className="error-message">
                  <ExclamationCircleOutlined className="error-icon" />
                  <span>{error}</span>
                </div>
              )}
              
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">
                    <UserOutlined />
                    الاسم الكامل *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="أدخل اسمك الكامل"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <MailOutlined />
                    البريد الإلكتروني *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="أدخل بريدك الإلكتروني"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <FileTextOutlined />
                    الموضوع *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="أدخل موضوع الرسالة"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">
                    <MessageOutlined />
                    الرسالة *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    required
                    placeholder="اكتب رسالتك هنا..."
                    rows="6"
                  />
                </div>
                
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <SendOutlined />
                      إرسال الرسالة
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      {mapEnabled && embedUrl && openMapUrl && (
        <div className="map-section">
          <div className="container">
            <div className="map-content">
              <h2 className="section-title">موقعنا</h2>
              <p className="section-description">
                يمكنك زيارتنا في مقرنا الرئيسي
              </p>

              <a
                href={openMapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="map-link"
                aria-label="افتح الموقع في جوجل مابس"
                title="افتح في جوجل مابس"
              >
                <div className="map-frame">
                  <iframe
                    src={embedUrl}
                    title="موقع المكتب الجغرافي"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <span className="map-open-hint">
                  <i className="fas fa-map-marker-alt"></i>
                  افتح في جوجل مابس
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ContactUsPage; 